import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Button } from "./button";
import { Badge } from "./badge";
import { Popover, PopoverContent, PopoverTrigger } from "ui/propertypopover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "ui/command";

export interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  closeOnSelect?: boolean;
  disabled?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select items...",
  className = "",
  closeOnSelect = false,
  disabled = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (value: string) => {
    let updated: string[];

    if (selected.includes(value)) {
      updated = selected.filter((item) => item !== value);
    } else {
      updated = [...selected, value];
    }

    onChange(updated);

    if (closeOnSelect) {
      setOpen(false);
    }
  };

  const handleRemove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((item) => item !== value));
  };

  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        if (!disabled) {
          setOpen(value);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between h-auto min-h-[36px] ${className}`}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selected.length === 0 ? (
              <span className="text-muted-foreground text-sm">
                {placeholder}
              </span>
            ) : (
              selected.map((value) => {
                const option = options.find((opt) => opt.value === value);
                return (
                  <Badge
                    key={value}
                    variant="secondary"
                    className="mr-1 text-xs"
                    style={{ backgroundColor: "#5B9BD5", color: "white" }}
                  >
                    {option?.label}
                    <span
                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onKeyDown={(e) => {
                        if (!disabled && e.key === "Enter") {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          handleRemove(value, e as any);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        if (!disabled) {
                          handleRemove(value, e);
                        }
                      }}
                    >
                      <X className="h-3 w-3 text-white hover:text-gray-200" />
                    </span>
                  </Badge>
                );
              })
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        sideOffset={4}
        collisionPadding={10}
        className="p-0"
      >
        <Command>
          <CommandInput
            placeholder="Search..."
            className="h-9 focus:outline-none focus:ring-0 focus:border-transparent"
          />
          <CommandEmpty>No item found.</CommandEmpty>
          <CommandGroup className="max-h-64 overflow-auto">
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.label}
                onSelect={() => {
                  if (!disabled) {
                    handleSelect(option.value);
                  }
                }}
              >
                <div
                  className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                    selected.includes(option.value)
                      ? "text-white"
                      : "border-gray-300 [&_svg]:invisible"
                  }`}
                  style={
                    selected.includes(option.value)
                      ? { backgroundColor: "#5B9BD5", borderColor: "#5B9BD5" }
                      : {}
                  }
                >
                  <Check className="h-4 w-4" />
                </div>
                <span>{option.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
